import { useLocale, useTranslation } from '@multaqa/i18n'
import { MoneyInput } from '@multaqa/ui/components/ui/money-input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { Switch } from '@multaqa/ui/components/ui/switch'
import type { WorkflowDocumentType } from './api'

const MODULES = ['hr', 'accounting', 'purchasing', 'inventory', 'payroll'] as const

export function WorkflowDocumentFields({
  documents,
  baseCurrency,
  documentType,
  thresholdAmount,
  thresholdEnabled,
  fieldError,
  onDocumentType,
  onThreshold,
  onThresholdEnabled,
}: {
  documents: WorkflowDocumentType[]
  baseCurrency: string
  documentType: string
  thresholdAmount: string
  thresholdEnabled: boolean
  fieldError: (field: 'document_type' | 'threshold_amount') => string | undefined
  onDocumentType: (type: WorkflowDocumentType | undefined) => void
  onThreshold: (amount: string) => void
  onThresholdEnabled: (enabled: boolean) => void
}) {
  const { t } = useTranslation('workflow')
  const { locale } = useLocale()
  const selected = documents.find((document) => document.key === documentType)
  const errorId = (field: string) => `workflow-${field.replace('_', '-')}-error`

  return (
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <label htmlFor="workflow-document-type" className="text-small">
          {t('form.documentType')}
        </label>
        <Select
          value={selected?.key ?? ''}
          onValueChange={(key) =>
            onDocumentType(documents.find((document) => document.key === key))
          }
        >
          <SelectTrigger
            id="workflow-document-type"
            aria-invalid={fieldError('document_type') ? true : undefined}
            aria-describedby={fieldError('document_type') ? errorId('document_type') : undefined}
          >
            <SelectValue placeholder={t('form.documentTypeRequired')} />
          </SelectTrigger>
          <SelectContent>
            {MODULES.map((module) => {
              const group = documents.filter((document) => document.module === module)
              if (group.length === 0) return null
              return (
                <SelectGroup key={module}>
                  <SelectLabel>{t(`form.moduleNames.${module}`)}</SelectLabel>
                  {group.map((document) => (
                    <SelectItem
                      key={document.key}
                      value={document.key}
                      disabled={document.in_use && document.key !== documentType}
                    >
                      <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                        <span>{document.label[locale]}</span>
                        {document.in_use && document.key !== documentType ? (
                          <span className="text-small text-muted-foreground">
                            {t('form.alreadyHasWorkflow')}
                          </span>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              )
            })}
          </SelectContent>
        </Select>
        <FieldError id={errorId('document_type')} message={fieldError('document_type')} />
      </div>

      {selected?.has_amount ? (
        <section className="grid gap-2 border border-border p-3">
          <label htmlFor="workflow-threshold-enabled" className="flex items-start gap-2 text-small">
            <Switch
              id="workflow-threshold-enabled"
              checked={thresholdEnabled}
              onCheckedChange={(checked) => {
                onThresholdEnabled(checked)
                onThreshold(checked ? thresholdAmount : '')
              }}
            />
            <span>{t('form.onlyWhenAmount')}</span>
          </label>
          {thresholdEnabled ? (
            <div className="grid gap-1.5">
              <label htmlFor="workflow-threshold" className="text-small">
                {t('form.threshold')}
              </label>
              <div className="flex min-w-0 items-center gap-2">
                <MoneyInput
                  id="workflow-threshold"
                  className="flex-1"
                  value={thresholdAmount}
                  locale={locale}
                  onChange={onThreshold}
                  dir="ltr"
                  aria-invalid={fieldError('threshold_amount') ? true : undefined}
                  aria-describedby={
                    fieldError('threshold_amount') ? errorId('threshold_amount') : undefined
                  }
                />
                <span className="shrink-0 tabular-nums text-small text-muted-foreground" dir="ltr">
                  {baseCurrency}
                </span>
              </div>
              <FieldError
                id={errorId('threshold_amount')}
                message={fieldError('threshold_amount')}
              />
              <p className="text-small text-muted-foreground">{t('form.baseCurrencyHelper')}</p>
            </div>
          ) : (
            <p className="text-small text-muted-foreground">
              {t('form.everyDocumentNeedsApproval')}
            </p>
          )}
        </section>
      ) : selected ? (
        <p className="text-small text-muted-foreground">{t('form.everyDocumentNeedsApproval')}</p>
      ) : null}
    </div>
  )
}

function FieldError({ id, message }: { id: string; message?: string }) {
  return message ? (
    <p id={id} className="text-small text-danger" role="alert">
      {message}
    </p>
  ) : null
}
